<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Loading
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="test_process.name" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete test process')"
              title="Delete test process"
              class="btn btn-danger ml-1"
              @click="showDeleteModal(test_process.id)"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read test process'">
      <template #gated-content>
        <test-process-card
          v-if="test_process_id"
          :test_process="test_process"
        />
        <h2>
          Process Tasks
          <b-button
            v-show="checkPermission('add test task')"
            variant="success"
            size="sm"
            :href="`#/test-processes/${test_process_id}/task/add/`"
            class="float-right ml-1"
            title="Add test task"
          >
            <i class="fal fa-plus" /> Task
          </b-button>
          <b-button
            v-show="checkPermission('edit test task')"
            variant="primary"
            size="sm"
            :href="`#/test-processes/${test_process_id}/sort/`"
            class="float-right ml-1"
            title="Sort test tasks"
          >
            <i class="fal fa-sort-numeric-down" /> Sort
          </b-button>
        </h2>
        <permission-alert-gate :permissions="'list test task'">
          <template #gated-content>
            <test-task-server-table @deleteTestTask="deleteTestTask" />
          </template>
        </permission-alert-gate>
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import TestProcessCard from "@/components/lab/testprocess/TestProcessCard";
import TestTaskServerTable from "@/components/lab/testtask/TestTaskServerTable";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { testProcessNotifications } from "@/shared/notifications";

const TestProcessRepository = LabRepositoryFactory.get("testProcess");
const TestTaskRepository = LabRepositoryFactory.get("testTask");

Vue.use(VueNotifications, notificationOptions);

export default {
  name: "TestProcessDetail",
  components: {
    BasicHeading,
    PermissionAlertGate,
    TestProcessCard,
    TestTaskServerTable,
    HeaderButtonGroup,
  },
  data() {
    return {
      loading: true,
      test_process_id: this.$route.params.test_process_id || null,
      test_process: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/test-processes",
            title: "Test Process list",
          },
          {
            icon: "fa-pencil",
            linkto: `/test-processes/${this.test_process_id}/edit`,
            title: "Edit Test Process",
            variant: "warning",
            permission: this.checkPermission("edit test process"),
          },
        ],
      };
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async deleteTestTask(id, ref) {
      try {
        const { data } = await TestTaskRepository.suspend(id);

        this.$bvToast.toast("Test Task deleted.", {
          title: data,
          variant: "success",
        });

        ref.refresh();
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Test Task delete Failed.",
          variant: "danger",
        });
      }
    },
    async deleteTestProcess(id) {
      try {
        await TestProcessRepository.suspend(id);

        this.$router.push("/test-processes");

        this.deleteSuccess();
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Test Process delete Failed.",
          variant: "danger",
        });
      }
    },

    showDeleteModal(id) {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this test process. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then((confirm) => {
          if (confirm) {
            this.deleteTestProcess(id);
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.test_process = await getModel({
      model_type: "test_process",
      id: this.test_process_id,
      message: this.$bvToast,
    });

    this.loading = false;
  },
  notifications: testProcessNotifications,
};
</script>
